import { LANDING_CATEGORIES, type LandingCategoryId } from '@/lib/landing-v1/types'

export { LANDING_CATEGORIES }
export type { LandingCategoryId }

/** Order the discovery grid renders in. `other` is the full-width catch-all. */
export const CATEGORY_ORDER: LandingCategoryId[] = [
  'ecommerce',
  'customer_support',
  'sales',
  'data_reporting',
  'marketing',
  'operations',
  'other',
]

export type Landing2Stage = 'categories' | 'questions' | 'contact' | 'done'

export const STAGE_ORDER: Landing2Stage[] = ['categories', 'questions', 'contact', 'done']

/* -------------------------------------------------------------------------- */
/*  Questions                                                                 */
/* -------------------------------------------------------------------------- */

type QuestionDefinition = { id: string; optionIds: string[] }

/**
 * Two category-specific questions each. `frequency` is asked once at the end of
 * the flow instead of repeating per category.
 */
const CATEGORY_QUESTIONS: Record<LandingCategoryId, QuestionDefinition[]> = {
  ecommerce: [
    {
      id: 'time_spent',
      optionIds: [
        'competitor_prices',
        'updating_products',
        'answering_customers',
        'creating_reports',
        'other',
      ],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'spreadsheets', 'website', 'multiple_tools', 'other'],
    },
  ],
  customer_support: [
    {
      id: 'support_volume',
      optionIds: [
        'high_volume',
        'repetitive_questions',
        'slow_response',
        'after_hours',
        'other',
      ],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'helpdesk', 'chat_widget', 'email_only', 'other'],
    },
  ],
  sales: [
    {
      id: 'sales_challenge',
      optionIds: ['finding_leads', 'qualifying_leads', 'follow_up', 'crm_updates', 'other'],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'crm', 'spreadsheets', 'multiple_tools', 'other'],
    },
  ],
  data_reporting: [
    {
      id: 'reporting_challenge',
      optionIds: ['manual_reports', 'scattered_data', 'slow_insights', 'error_prone', 'other'],
    },
    {
      id: 'current_process',
      optionIds: ['spreadsheets', 'dashboards', 'manual_exports', 'multiple_tools', 'other'],
    },
  ],
  marketing: [
    {
      id: 'marketing_challenge',
      optionIds: [
        'content_creation',
        'social_posting',
        'campaign_tracking',
        'audience_research',
        'other',
      ],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'agencies', 'scheduling_tools', 'multiple_tools', 'other'],
    },
  ],
  operations: [
    {
      id: 'operations_challenge',
      optionIds: ['data_entry', 'approvals', 'inventory', 'scheduling', 'other'],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'spreadsheets', 'erp', 'multiple_tools', 'other'],
    },
  ],
  other: [
    {
      id: 'problem_description',
      optionIds: ['repetitive_workflow', 'team_overloaded', 'data_hard_to_use', 'other'],
    },
    {
      id: 'current_process',
      optionIds: ['manually', 'spreadsheets', 'multiple_tools', 'not_handled'],
    },
  ],
}

const FREQUENCY_QUESTION: QuestionDefinition = {
  id: 'frequency',
  optionIds: ['daily', 'several_weekly', 'occasionally'],
}

/** Free-text follow-up is offered for these option ids. */
export const FREE_TEXT_OPTION_IDS = ['other'] as const

export type Landing2QuestionStep = {
  /** Stable key used for the answers record sent to the API. */
  key: string
  /** Lookup key into `copy.questionText`. */
  copyKey: string
  optionIds: string[]
}

/** Keeps the flow short enough to finish: at most four specific questions. */
const MAX_SPECIFIC_QUESTIONS = 4

export function buildQuestionFlow(selected: LandingCategoryId[]): Landing2QuestionStep[] {
  const specific = selected.flatMap((categoryId) =>
    CATEGORY_QUESTIONS[categoryId].map((question) => ({
      key: `${categoryId}.${question.id}`,
      copyKey: `${categoryId}.${question.id}`,
      optionIds: question.optionIds,
    })),
  )

  if (specific.length === 0) return []

  return [
    ...specific.slice(0, MAX_SPECIFIC_QUESTIONS),
    {
      key: 'shared.frequency',
      copyKey: 'shared.frequency',
      optionIds: FREQUENCY_QUESTION.optionIds,
    },
  ]
}

/* -------------------------------------------------------------------------- */
/*  Workflow graph                                                            */
/* -------------------------------------------------------------------------- */

export type WorkflowNodeKind = 'source' | 'agent' | 'step' | 'output'

/** Every category workflow is the same five-node shape, so the rail reads as one system. */
export const WORKFLOW_NODE_KINDS: readonly WorkflowNodeKind[] = [
  'source',
  'agent',
  'step',
  'step',
  'output',
]

/** Categories that get a dedicated module card in "What can Atirame automate?". */
export const MODULE_CATEGORY_IDS: LandingCategoryId[] = [
  'ecommerce',
  'customer_support',
  'sales',
  'data_reporting',
]

/* -------------------------------------------------------------------------- */
/*  Agent network                                                             */
/* -------------------------------------------------------------------------- */

export const AGENT_SOURCE_IDS = ['website', 'store', 'inbox', 'sheets'] as const
export type AgentSourceId = (typeof AGENT_SOURCE_IDS)[number]

export const AGENT_ACTION_IDS = [
  'reply_sent',
  'price_updated',
  'report_delivered',
  'lead_scored',
] as const
export type AgentActionId = (typeof AGENT_ACTION_IDS)[number]

/* -------------------------------------------------------------------------- */
/*  Contact validation                                                        */
/* -------------------------------------------------------------------------- */

export type ContactMethodId = 'telegram' | 'phone' | 'email'

export const CONTACT_METHOD_IDS: ContactMethodId[] = ['email', 'telegram', 'phone']

const TELEGRAM_PATTERN = /^@?[a-zA-Z0-9_]{4,32}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * Returns the error key to look up in `copy.contact.errors`, or null when valid.
 * Phone numbers are validated per country in `lib/landing-v2/phone.ts`.
 */
export function validateContact(
  method: Exclude<ContactMethodId, 'phone'>,
  value: string,
): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'required'
  if (method === 'telegram') return TELEGRAM_PATTERN.test(trimmed) ? null : 'telegram'
  return EMAIL_PATTERN.test(trimmed) ? null : 'email'
}
